<template>
    <div class="day-planning">
        <div
            v-for="date in dates"
            :key="date.formatted"
            class="day-planning__day"
        >
            <div
                v-for="slot in slotsForDate(date)"
                :key="slot.id"
                class="day-planning__row"
                @drop.prevent="handleSlotDrop($event, slot)"
            >
                <div class="day-planning__title-row">
                    <span class="day-planning__title">{{ slot.task_name }}</span>
                    <div class="day-planning__title-buttons">
                        <button
                            class="main-color"
                            @click="openPlanningForm(slot)"
                        >
                            <i class="icofont-calendar"></i>
                        </button>
                    </div>
                </div>

                <div class="day-planning__assets" @click="openPlanningForm(slot)">
                    <div
                        class="day-planning__asset"
                        v-for="person in getSlotPersons(slot)"
                        :key="slot.id +'-'+person.id"
                        :style="{...assetStyle(person), ...getBorderColorForDateAndPerson(slot.date, person)}"
                        :draggable="true"
                        @dragstart="handleDragStartPerson($event, slot, person)"
                        @mouseenter="!isDragging && showPersonTooltip($event, person.id, slot.date)"
                        @mouseleave="!isDragging && hidePersonTooltip($event, person.id)"
                    >
                        {{ person.name }}
                        <span
                            class="status-dot"
                            :class="
                                person.badge_type === 'in' ? 'green' : 'red'
                            "
                        ></span>
                    </div>
                    <div
                        class="day-planning__asset"
                        v-for="equipment in getSlotEquipments(slot)"
                        :key="slot.id +'-'+equipment.id"
                        :style="assetStyle(equipment)"
                        :draggable="true"
                        @dragstart="handleDragStartEquipment($event, slot, equipment)"
                    >
                        {{ equipment.title }}
                    </div>
                </div>

            </div>
            <div
                class="day-planning__empty-dropszone"
                @drop.prevent="handleDropzoneDrop($event, date)"
            >
            </div>
            <div
                class="title-row"
                v-if="absences[date.formatted] && Object.keys(absences[date.formatted]).length"
                >
                <div class="title-row-content">
                    {{ trans('Afwezigheden') }}
                </div>
            </div>
            <div
                v-for="(absencesForType, typeId) in getAbsencesGroupedByType(date.formatted)"
                :key="typeId"
                class="day-planning__row"
            >
                <div class="day-planning__title-row">
                    <span class="day-planning__title">{{ getAbsenceTypeName(absencesForType[0]) }}</span>
                </div>

                <div class="day-planning__assets">
                    <div
                        class="day-planning__asset"
                        v-for="personId in absencesForType.map(a => Number(a.person_id))"
                        :key="personId"
                        :style="assetStyle(getPersonById(personId))"
                    >
                        {{ getPersonName(personId) }}
                        <span class="status-dot red"></span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <PersonTooltipComponent
        ref="personTooltip"
    />
</template>


<script>
import {mapState} from "vuex";
import axios from "axios";
import {emitter} from "../../../plugins/Mitt";
import PersonTooltipComponent from "../../person/PersonTooltipComponent.vue";

export default {
    components: {
        PersonTooltipComponent
    },
    data(){
        return {
            slots: [],
            persons: [],
            equipments: [],
            absences: {},
            defaultWeekschedules: {},
            plannedWeekschedules: {},
            isDragging: false
        }
    },
    computed: {
        ...mapState('planning', ['dateStart', 'dateEnd', 'dates']),
        ...mapState('planningFilter', ['filter','filterLoaded']),
    },
    methods:{
        showPersonTooltip(event, personId, date) {
            this.$refs.personTooltip.activateTooltipForPerson(event, personId, date);
        },
        hidePersonTooltip(event, personId) {
            this.$refs.personTooltip.deactivateOrCancelTooltip(event, personId);
        },
        getSlots() {
            if( !this.filterLoaded || !this.dateStart || !this.dateEnd){
                return;
            }
            const start = this.dateStart;
            const end = this.dateEnd;

            const url = route('planning.daily-planned.slots');

            const allowedFilters = ['department_ids', 'team_ids'];
            const filter = Object.fromEntries(
                Object.entries(this.filter)
                    .filter(([key]) => allowedFilters.includes(key))
            );

            const params = {
                date_start: start,
                date_end: end,
                filter: filter,
            };

            axios.get(url, {params})
                .then(response => {
                    this.slots = response.data.slots;
                    this.persons = response.data.persons;
                    this.equipments = response.data.equipments;
                    this.absences = response.data.absences;
                    this.defaultWeekschedules = response.data.default_weekschedules;
                    this.plannedWeekschedules = response.data.planned_weekschedules;
                });
        },
        getSlotPersons(slot) {
            // Get persons for this slot and sort them by job_id first, then by name
            return this.persons
                .filter(person => slot.person_ids.includes(person.id))
                .sort((a, b) => {
                    // Sort by job_id first (null values last)
                    if (a.job_id === null && b.job_id !== null) return 1;
                    if (a.job_id !== null && b.job_id === null) return -1;
                    if (a.job_id !== b.job_id) return a.job_id - b.job_id;
                    
                    // If job_id is the same, sort by name
                    return a.name.localeCompare(b.name);
                });
        },
        getSlotEquipments(slot) {
            return this.equipments.filter(equipment => slot.equipment_ids.includes(equipment.id));
        },
        getPersonName(personId) {
            const person = this.persons.find(p => p.id === personId);
            return person ? person.name : '';
        },
        slotsForDate(date){
            return this.slots.filter(slot => {
                return slot.date === date.formatted;
            });
        },
        assetStyle(person){
            return {
                background: person.status_color,
                color: person.status_text_color,
            }
        },
        orderedSlotPersons(persons) {
            return persons.sort((a, b) => {
                if (a.job_id === null) return 1;
                if (b.job_id === null) return -1;
                return a.job_id - b.job_id;
            });
        },
        openPlanningForm(slot){
            emitter.emit('open-planning-form', {
                slotId: slot.id,
            });
        },
        handleDropzoneDrop($event, date){
            let personId = $event.dataTransfer.getData('personId');

            const defaultWeekschedule = this.defaultWeekschedules[date.formatted];
            let day = defaultWeekschedule || { time_start: '08:00', time_end: '16:00' };

            let eventObject = {
                dateStart: date.formatted,
                dateEnd: date.formatted,
                timeStart: day.time_start,
                timeEnd: day.time_end,
            };
            if( personId ){
                eventObject.personId = personId;
            }

            emitter.emit('open-planning-form', eventObject);
        },
        handleSlotDrop($event, slot){
            let personId = Number($event.dataTransfer.getData('personId'));
            let equipmentId = Number($event.dataTransfer.getData('equipmentId'));
            const startSlotId = Number($event.dataTransfer.getData('startSlotId'));

            if( !personId && !equipmentId ){
                return;
            }

            if( slot.id === startSlotId ){
                return;
            }

            let assets = {};

            if( personId ){
                let personIds = [...slot.person_ids];
                personIds.push(personId);
                assets['personIds'] = personIds;
            }

            if( equipmentId ){
                let equipmentIds = [...slot.equipment_ids];
                equipmentIds.push(equipmentId);
                assets['equipmentIds'] = equipmentIds;
            }

            const url = route('planning.sync-resources', {'slotId': slot.id});
            axios.post(url, assets).then((response) => {
                if( startSlotId ){
                    this.removeAssetsFromSlot(startSlotId, personId, equipmentId)
                    return;
                }
                this.getSlots()
                emitter.emit('planning-drop-handled');
            });
        },
        removeAssetsFromSlot(slotId, personId, equipmentId){
            let slot = this.slots.find(slot => slot.id === slotId);
            if( !slot || ( !personId && !equipmentId ) ){
                return;
            }

            let assets = {};

            if( personId ){
                let personIds = slot.person_ids.filter(id => id !== personId);
                assets['personIds'] = personIds;
            }

            if( equipmentId ){
                let equipmentIds = slot.equipment_ids.filter(id => id !== equipmentId);
                assets['equipmentIds'] = equipmentIds;
            }

            const url = route('planning.sync-resources', {'slotId': slot.id});
            axios.post(url, assets).then((response) => {
                this.getSlots()
            });
        },
        handleDragStartPerson($event, slot, person){
            const body = document.body;
            body.classList.add('cursor-grabbing');
            this.isDragging = true;

            // Hide any active tooltip when dragging starts
            this.hidePersonTooltip($event, person.id);

            $event.dataTransfer.setData('startSlotId', slot.id);
            $event.dataTransfer.setData('personId', person.id);
        },
        handleDragStartEquipment($event, slot, equipment){
            const body = document.body;
            body.classList.add('cursor-grabbing');
            this.isDragging = true;

            $event.dataTransfer.setData('startSlotId', slot.id);
            $event.dataTransfer.setData('equipmentId', equipment.id);
        },
        getAbsenceTypeName(absence) {
            return absence.type_name;
        },
        getAbsencesGroupedByType(date) {
            const absencesForDate = this.absences[date] || {};
            const groupedByType = {};

            Object.entries(absencesForDate).forEach(([personId, personAbsences]) => {
                // Iterate through all absences for this person, not just the first one
                personAbsences.forEach(absence => {
                    const typeId = absence.type_id;
                    if (!groupedByType[typeId]) {
                        groupedByType[typeId] = [];
                    }
                    groupedByType[typeId].push({
                        ...absence,
                        person_id: personId
                    });
                });
            });

            return groupedByType;
        },
        getPersonById(personId) {
            return this.persons.find(p => p.id === personId) || {
                status_color: '#ccc',
                status_text_color: '#000000'
            };
        },
        getWeekschedulForDateAndPerson(date, person) {
            if (!person?.id) return null;
            
            let weekSchedule = this.plannedWeekschedules[person.id]?.[date] ?? null;
            if (!weekSchedule) {
                weekSchedule = this.defaultWeekschedules?.[date] ?? null;
            }
            return weekSchedule;
        },

        getBorderColorForDateAndPerson(date, person) {
            if (!person?.id) return {};

            const weekSchedule = this.getWeekschedulForDateAndPerson(date, person);
            if (!weekSchedule || !weekSchedule.is_working) {
                return {
                    borderColor: '#F00',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
            }

            if (this.isPersonOverplanned(date, person)) {
                return {
                    borderColor: '#F00',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
            }

            return {};
        },

        isPersonOverplanned(date, person) {
            if (!person?.id) return false;

            const weekSchedule = this.getWeekschedulForDateAndPerson(date, person);
            const absencesForPerson = this.absences[date]?.[person.id] ?? [];
            let timeToWorkInitially = weekSchedule?.duration ?? 0;
            let timePlanned = 0;

            // Subtract absences
            absencesForPerson.forEach(absence => {
                timeToWorkInitially -= absence.duration;
            });

            // Calculate planned time from slots
            const slotsForDate = this.slots.filter(slot => slot.date === date);
            slotsForDate.forEach(slot => {
                if (slot.person_ids.includes(person.id)) {
                    const start = new Date(`2000-01-01 ${slot.start_time}`);
                    const end = new Date(`2000-01-01 ${slot.end_time}`);
                    timePlanned += (end - start) / (1000 * 60); // Convert to minutes
                }
            });

            // If not working, any planned time is overplanned
            if (!weekSchedule?.is_working) {
                return timePlanned > 0;
            }

            return timeToWorkInitially - timePlanned < 0;
        },
    },
    watch: {
        dates: function () {
            this.getSlots();
        },
        filter: {
            handler: function () {
                this.getSlots();
            },
            deep: true
        },
        filterLoaded: function () {
            this.getSlots();
        },
    },
    created() {
        this.getSlots();

        // Store reference to the handler so we can remove it specifically
        this.handlePlanningSlots = () => {
            this.getSlots();
        };

        this.handleDragEnd = () => {
            this.isDragging = false;
        };

        emitter.on('planning-slots-changed', this.handlePlanningSlots);
        emitter.on('drag-end', this.handleDragEnd);
    },
    beforeUnmount() {
        emitter.off('planning-slots-changed', this.handlePlanningSlots);
        emitter.off('drag-end', this.handleDragEnd);
    }
}
</script>

<style scoped lang="scss">
.day-planning{
    display: inline-flex;
    height: calc(100vh - 100px);
    width: 100%;
}

.day-planning__day {
    display: flex;
    flex-direction: column;
    border-right: 1px solid #ccc;
    // border-left: 1px solid #ccc;
}

.day-planning__row{
    flex: 0 0 auto;
    display: inline-flex;
    border-top: 1px solid #ccc;
}

.day-planning__title-row{
    box-sizing: border-box;
    position: sticky;
    left: 0;
    width: 250px;
    background: #fff;
    border-right: 1px solid #ccc;
    padding-left: 10px;
    z-index: 100;
    display: flex;
    justify-content: space-between;
}
.day-planning__title{
    padding-top: 9px;
}
.day-planning__title-buttons{
    padding-top: 9px
}

.day-planning__assets{
    background: #FFF;
    width: 897px;
    box-sizing: border-box;
    line-height: 25px;
    text-transform: uppercase;
    padding: 5px;
    padding-bottom: 0;
}
.day-planning__asset{
    display: inline-block;
    background: #ccc;
    border-radius: 5px;
    padding: 0px 5px;
    margin-right: 5px;
    margin-bottom: 5px;
    border: 2px solid transparent;
}

.day-planning__empty-dropszone{
    width: 1147px;
    box-sizing: border-box;
    min-height: 50px;
    flex: 1 1 auto;
    background: #FFF;
    border-top: 1px solid #ccc;
}
</style>